import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import {
  TextField,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  MenuItem,
  Slide,
  Paper
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { LocationVerificationMap, LocationVerificationGrid } from 'components/underwrite/acquisition/import/importOpportunities/LocationVerification';
import { CreatePropMasterLocation } from 'api/revue/import';
import { LoadingOverlayWrapper } from 'components/layout';
import { appNotify } from 'store/actions';
import { validateLatAndLong } from 'api/revue/import';
import { sendClientLog } from 'services/logService';

const stateAbbreviations = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL',
  'GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
 ];

// Friendlier explanations for PropertyMaster JIT statuses that refuse to create a property
const jitFailureMessages = {
  NearMatchesFound: 'This location is too close to / ambiguous with an existing property - see near matches below',
  MalformedJITAddress: 'Address format not accepted by PropertyMaster (check zip code / house number)',
  JitCollision: 'Same address was submitted twice in this batch - resubmit'
};

const LocationVerificationModalNew = ({ classes, appNotify, open, onClose, onParentClose, geoValidationDto, updateLocation, jitCreationType, searchWithPm }) => {
  const [pmCreateIsLoading, setPmCreateIsLoading] = useState(false);
  const [geoResultDto, setGeoResultDto] = useState(null);
  const [createAddress, setCreateAddress] = useState({
    latitude: geoValidationDto.latitude,
    longitude: geoValidationDto.longitude,
    street: geoValidationDto.street,
    city: geoValidationDto.city,
    state: geoValidationDto.state,
    zipCode: geoValidationDto.zipCode
  });

  const [isLatLngValid, setIsLatLngValid] = useState(false);

  const validateInitialLagAndLong = async () => {
    var r = await validateLatAndLong([{ lat: geoValidationDto.latitude, long: geoValidationDto.longitude }]);
    setIsLatLngValid(r[0].isValid)
  }

  useEffect(() => {
    validateInitialLagAndLong();
  }, [])

  const handleAddressChange = (value, field) => {
    if (geoResultDto) {
      setGeoResultDto(null);
    }
    setCreateAddress({
      ...createAddress,
      [field]: value
    });
  }

  const trimValue = field => {
    const trimmed = createAddress[field].trim().replace(/  +/g, ' '); // Trim and replace any double spaces with single space
    if (createAddress[field] !== trimmed) {
      handleAddressChange(trimmed, field);
    }
  }

  const updateLatLng = async (lat, long) => {
    var r = await validateLatAndLong([{lat, long }]);
    setIsLatLngValid(r[0].isValid)
    if (geoResultDto) {
      if (geoResultDto.locationCreated) return; // Don't allow updating lat/lng once property is successfully created
      setGeoResultDto(null);
    }
      setCreateAddress(createAddress => {
      return {
        ...createAddress,
        latitude: lat,
        longitude: long
      }
    })
  }

  const handleCreateProperty = () => {
    const req = {
      referenceId: 0,
      latitude: createAddress.latitude,
      longitude: createAddress.longitude,
      street: createAddress.street,
      city: createAddress.city,
      state: createAddress.state,
      zipCode: createAddress.zipCode
    }
    setPmCreateIsLoading(true);
    if (geoResultDto) {
      setGeoResultDto(null);
    }
    CreatePropMasterLocation(req, jitCreationType).then(res => {
      setPmCreateIsLoading(false);
      setGeoResultDto(res);
      if (res.locationCreated) { 
        appNotify('Property Created', 'success');
        // updateLocation(res);
        const geoResultDto = {
          ...res,
          propertyMasterResult: { // Always save user inputs instead of PM results for these fields
            ...res.propertyMasterResult,
            latitude: createAddress.latitude,
            longitude: createAddress.longitude,
            cleanAddress: createAddress.street,
            city: createAddress.city,
            state: createAddress.state,
            zip: createAddress.zipCode
          }
        }
        updateLocation(geoResultDto);
        sendClientLog('Info', `Import - User clicked Create and Use Property in Location Verification for address ${createAddress.street}, PM ID ${res.propertyMasterResult.asgPropID}`);
      } else {
        appNotify('Unable to create property', 'warning');
      }
    }).catch(err => {
      console.log(err);
      appNotify(`An error occurred - ${err.message || err}`, 'error');
      setPmCreateIsLoading(false);
    });
  }

  const handleSearchNearMatch = (req, searchBoxAddress) => { // Go back to the search view and load the colliding property
    onClose();
    searchWithPm(req, searchBoxAddress);
  }

  const getFailureMessage = ({ status, asgPropID }) => {
    const friendly = jitFailureMessages[status];
    return `Unable to create property. ${friendly ? `${friendly}. ` : ''}PM status response: ${status}${asgPropID ? `. PM/asgPropID: ${asgPropID}` : ''}`;
  }

  const getButtons = () => {
    const BackToSearch = () => <Button variant="outlined" color="primary" onClick={onClose} disabled={pmCreateIsLoading}>Back to Search</Button>
    const Close = () => <Button variant="contained" color="primary" onClick={onParentClose} disabled={pmCreateIsLoading}>Close</Button>
    const CreateProperty = () => <Button variant="contained" color="primary" onClick={handleCreateProperty} disabled={!isLatLngValid || someFieldIsEmpty || someFieldExceedsLength || pmCreateIsLoading || (geoResultDto && !geoResultDto.locationCreated)}>Create and Use Property</Button>

    if (geoResultDto?.locationCreated) { // JIT id created
      return <Close />
    }

    return ( // No create attempted yet, or create returns a property with status that fails validation
      <>
        <BackToSearch />
        <CreateProperty />
      </>
    )
  }

  const isNullorEmptyString = val => {
    return val == null || val === '';
  }

  const exceedsLength = val => {
    return val?.length > 100;
  }

  const someFieldIsEmpty = ['latitude', 'longitude', 'street', 'city', 'state', 'zipCode'].some(field => isNullorEmptyString(createAddress[field]));
  const someFieldExceedsLength = ['street', 'city'].some(field => exceedsLength(createAddress[field]));

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper elevation={4} className={classes.paper}>
        <LoadingOverlayWrapper
          active={pmCreateIsLoading}
          message={'Creating Property...'}
        >
          <div style={{ width: '100%', height: 500 }}>
            <LocationVerificationMap
              currentPin={{
                lat: createAddress.latitude,
                lng: createAddress.longitude,
                maintainZoom: true
              }}
              updateLatLng={updateLatLng}
              classes={classes}
              autoCompleteEnabled
              replaceCurrentAddress={setCreateAddress}
            />
          </div>
        </LoadingOverlayWrapper>
        <DialogContent>
          <div>
            <TextField
              label='Street'
              className={classes.textFieldRoot}
              value={createAddress.street}
              onChange={e => handleAddressChange(e.target.value, 'street')}
              style={{ width: 500 }}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              error={isNullorEmptyString(createAddress.street) || exceedsLength(createAddress.street)}
              helperText={isNullorEmptyString(createAddress.street) ? 'Street is required' : exceedsLength(createAddress.street) ? 'Street cannot exceed 100 characters' : ''}
              onBlur={() => trimValue('street')}
            />
            <TextField
              label='Latitude'
              className={classes.textFieldRoot}
              value={createAddress.latitude ?? ''}
              onChange={e => handleAddressChange(e.target.value, 'latitude')}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              error={isNullorEmptyString(createAddress.latitude) || !isLatLngValid}
              helperText={isNullorEmptyString(createAddress.latitude) ? 'Latitude is required' : ''}
              InputProps={{readOnly : true}}
            />
            <TextField
              label='Longitude'
              className={classes.textFieldRoot}
              value={createAddress.longitude ?? ''}
              onChange={e => handleAddressChange(e.target.value, 'longitude')}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              error={isNullorEmptyString(createAddress.longitude) || !isLatLngValid}
              helperText={isNullorEmptyString(createAddress.longitude) ? 'Longitude is required' : ''}
              InputProps={{readOnly : true}}
            />
          </div>
          <div>
            <TextField
              label='City'
              className={classes.textFieldRoot}
              value={createAddress.city}
              onChange={e => handleAddressChange(e.target.value, 'city')}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              error={isNullorEmptyString(createAddress.city) || exceedsLength(createAddress.city)}
              helperText={isNullorEmptyString(createAddress.city) ? 'City is required' : exceedsLength(createAddress.city) ? 'City cannot exceed 100 characters' : ''}
              onBlur={() => trimValue('city')}
            />
            <TextField
              label='State'
              className={classes.textFieldRoot}
              value={createAddress.state}
              onChange={e => handleAddressChange(e.target.value, 'state')}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              select
              error={isNullorEmptyString(createAddress.state)}
              helperText={isNullorEmptyString(createAddress.state) ? 'State is required' : ''}
            >
              {stateAbbreviations.map(state => {
                return <MenuItem key={state} value={state}>{state}</MenuItem>
              })}
            </TextField>
            <NumberFormat
              label='Zip Code'
              className={classes.textFieldRoot}
              decimalScale={0}
              allowNegative={false}
              format='#####'
              onValueChange={values => handleAddressChange(values.value, 'zipCode')}
              value={createAddress.zipCode}
              customInput={TextField}
              disabled={pmCreateIsLoading || geoResultDto?.locationCreated}
              error={isNullorEmptyString(createAddress.zipCode)}
              helperText={isNullorEmptyString(createAddress.zipCode) ? 'Zip Code is required' : ''}
              onBlur={() => trimValue('zipCode')}
            />
          </div>
        </DialogContent>
        {geoResultDto &&
          <Typography style={{ textAlign: 'right', margin: '0 8px', color: geoResultDto.locationCreated ? 'inherit' : 'red'}}>
            {geoResultDto.locationCreated
              ? `Property successfully created with PM/asgPropID ${geoResultDto.propertyMasterResult.asgPropID}`
              : getFailureMessage(geoResultDto.propertyMasterResult)
            }
          </Typography>
        }
        {geoResultDto && !geoResultDto.locationCreated &&
          <div style={{ margin: '8px 8px 0' }}>
            <LocationVerificationGrid nearMatches={geoResultDto.propertyMasterResult?.nearMatches} searchWithPm={handleSearchNearMatch} width='100%' />
          </div>
        }
        <DialogActions>
          {getButtons()}
        </DialogActions>
        
      </Paper>
    </Slide>
  )
};
const styles = theme => ({
  paper: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto', // near-match grid can push content past the panel height
  },
  textFieldRoot: {
    margin: 8,
    height: 68
  },
});

const mapDispatchToProps = dispatch => {
  return {
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
  }
};

export default compose(
  withStyles(styles),
  connect(null, mapDispatchToProps)
)(LocationVerificationModalNew);